// Small helpers shared by the environment builders: canvas textures, boxes with UVs in
// metres, geometry merging and GLSL noise.
import {
  BoxGeometry, BufferAttribute, BufferGeometry, CanvasTexture, Euler, Matrix4, Quaternion,
  RepeatWrapping, SRGBColorSpace, Vector3, type Texture,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export { rng } from './layout';

/** Draws into a fresh canvas and wraps it as a texture (sRGB unless `data` is set). */
export function canvasTexture(
  w: number, h: number, draw: (g: CanvasRenderingContext2D, w: number, h: number) => void,
  opts: { data?: boolean; repeat?: boolean; anisotropy?: number } = {},
): CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const g = c.getContext('2d')!;
  draw(g, w, h);
  const t = new CanvasTexture(c);
  if (!opts.data) t.colorSpace = SRGBColorSpace;
  if (opts.repeat) t.wrapS = t.wrapT = RepeatWrapping;
  t.anisotropy = opts.anisotropy ?? 8;
  return t;
}

export interface BoxOpts {
  /** Centre position. */
  at?: [number, number, number];
  /** Euler rotation in radians. */
  rot?: [number, number, number];
  /** Texture tile size in metres; UVs become world-scaled so wood grain keeps its size. */
  tile?: number;
  /** Vertex colour (used as baked ambient occlusion / tint), 0..1. */
  shade?: number | [number, number, number];
  /** Which way the texture's u axis runs on the box's long side: 'x' (default) or 'y'. */
  grain?: 'x' | 'y';
}

/** A box with UVs in metres (divided by `tile`) and an optional vertex colour. */
export function box(w: number, h: number, d: number, o: BoxOpts = {}): BufferGeometry {
  const g = new BoxGeometry(w, h, d).toNonIndexed();
  const uv = g.getAttribute('uv') as BufferAttribute;
  const tile = o.tile ?? 2;
  // Face order: +x, −x, +y, −y, +z, −z; 6 vertices each after toNonIndexed.
  const dims: [number, number][] = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
  for (let f = 0; f < 6; f++) {
    const [su, sv] = dims[f]!;
    const side = f !== 2 && f !== 3;
    for (let i = 0; i < 6; i++) {
      const k = f * 6 + i;
      let u = uv.getX(k) * su / tile, v = uv.getY(k) * sv / tile;
      if (o.grain === 'y' && side) [u, v] = [v, u];
      uv.setXY(k, u, v);
    }
  }
  shade(g, o.shade ?? 1);
  if (o.rot || o.at) {
    const m = new Matrix4().compose(
      new Vector3(...(o.at ?? [0, 0, 0])),
      new Quaternion().setFromEuler(new Euler(...(o.rot ?? [0, 0, 0]))),
      new Vector3(1, 1, 1),
    );
    g.applyMatrix4(m);
  }
  return g;
}

/** Sets (or replaces) a per-vertex colour on a geometry. */
export function shade(g: BufferGeometry, s: number | [number, number, number]): BufferGeometry {
  const n = g.getAttribute('position').count;
  const [r, gg, b] = typeof s === 'number' ? [s, s, s] : s;
  const a = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { a[i * 3] = r; a[i * 3 + 1] = gg; a[i * 3 + 2] = b; }
  g.setAttribute('color', new BufferAttribute(a, 3));
  return g;
}

/** Makes a geometry non-indexed with position, normal, uv and color so it can be merged. */
export function normalise(g: BufferGeometry, s: number | [number, number, number] = 1): BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g;
  if (!out.getAttribute('normal')) out.computeVertexNormals();
  if (!out.getAttribute('uv')) out.setAttribute('uv', new BufferAttribute(new Float32Array(out.getAttribute('position').count * 2), 2));
  if (!out.getAttribute('color')) shade(out, s);
  for (const k of Object.keys(out.attributes)) if (!['position', 'normal', 'uv', 'color'].includes(k)) out.deleteAttribute(k);
  return out;
}

/** Merges geometries that share position, normal, uv and color. */
export function merge(parts: BufferGeometry[]): BufferGeometry {
  const g = mergeGeometries(parts.map((p) => normalise(p)), false);
  if (!g) throw new Error('environment: geometry merge failed');
  for (const p of parts) p.dispose();
  return g;
}

/** Applies a translation/rotation/scale to a geometry in place. */
export function place(g: BufferGeometry, at: [number, number, number], rot: [number, number, number] = [0, 0, 0], scale: [number, number, number] = [1, 1, 1]): BufferGeometry {
  g.applyMatrix4(new Matrix4().compose(new Vector3(...at), new Quaternion().setFromEuler(new Euler(...rot)), new Vector3(...scale)));
  return g;
}

/** Sets the wrapping, repeat-ready state and anisotropy of a loaded texture. */
export function tiled(t: Texture, anisotropy = 8): Texture {
  t.wrapS = t.wrapT = RepeatWrapping;
  t.anisotropy = anisotropy;
  t.needsUpdate = true;
  return t;
}

/** GLSL hash, value noise and fbm used by several environment shaders. */
export const GLSL_NOISE = /* glsl */ `
float envHash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float envNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(envHash(i), envHash(i + vec2(1, 0)), u.x), mix(envHash(i + vec2(0, 1)), envHash(i + vec2(1, 1)), u.x), u.y);
}
float envFbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * envNoise(p); p = p * 2.03 + vec2(17.1, 9.2); a *= 0.5; }
  return v;
}
`;

/**
 * Turf colour shared by the ground and the grass blades, so they read as one lawn. Pass
 * mid = envFbm(wp * 0.22 + 3.1) and big = envFbm(wp * 0.035) for world position wp.
 * Materials that use it must bind uDryGrass and uSnow from SEASON_UNIFORMS.
 */
export const GLSL_TURF = /* glsl */ `
uniform float uDryGrass; // season: faded autumn and early-spring grass
uniform float uSnow;     // season: snow on the ground
vec3 turfColour(float mid, float big) {
  vec3 col = mix(vec3(0.07, 0.23, 0.035), vec3(0.17, 0.36, 0.06), smoothstep(0.3, 0.75, mid));
  col = mix(col, vec3(0.33, 0.33, 0.11), smoothstep(0.55, 0.85, big) * 0.4);
  vec3 dry = mix(vec3(0.2, 0.22, 0.08), vec3(0.36, 0.3, 0.13), smoothstep(0.3, 0.8, mid));
  return mix(col, dry, uDryGrass * (0.45 + 0.4 * smoothstep(0.35, 0.75, big)));
}
`;

/** Draws a simple cat paw print (four toe beans and a pad) centred at (x, y) with size s. */
export function drawPaw(g: CanvasRenderingContext2D, x: number, y: number, s: number, colour: string): void {
  g.save();
  g.fillStyle = colour;
  g.beginPath(); g.ellipse(x, y + s * 0.18, s * 0.34, s * 0.28, 0, 0, Math.PI * 2); g.fill();
  const toes: [number, number, number][] = [[-0.36, -0.14, 0.12], [-0.13, -0.36, 0.13], [0.13, -0.36, 0.13], [0.36, -0.14, 0.12]];
  for (const [tx, ty, r] of toes) { g.beginPath(); g.ellipse(x + tx * s, y + ty * s, r * s, r * s * 1.25, tx * 0.6, 0, Math.PI * 2); g.fill(); }
  g.restore();
}

/** Draws a five-point crown centred at (x, y), width w. */
export function drawCrown(g: CanvasRenderingContext2D, x: number, y: number, w: number, colour: string): void {
  const h = w * 0.62;
  g.save();
  g.fillStyle = colour;
  g.beginPath();
  g.moveTo(x - w / 2, y + h / 2);
  g.lineTo(x - w / 2, y - h * 0.15);
  g.lineTo(x - w * 0.27, y + h * 0.08);
  g.lineTo(x, y - h / 2);
  g.lineTo(x + w * 0.27, y + h * 0.08);
  g.lineTo(x + w / 2, y - h * 0.15);
  g.lineTo(x + w / 2, y + h / 2);
  g.closePath();
  g.fill();
  for (const px of [-0.5, 0, 0.5]) { g.beginPath(); g.arc(x + px * w, y - (px === 0 ? h / 2 : h * 0.15), w * 0.07, 0, Math.PI * 2); g.fill(); }
  g.restore();
}

/** The chunky display font stack used for painted signs (system fonts only; nothing to load). */
export const SIGN_FONT = `'Arial Black', 'Helvetica Neue', Impact, system-ui, sans-serif`;

/** UI palette from ui/index.md: plum panels, ivory text, gold accents, plus the banner maroon. */
export const PALETTE = { plum: '#211923', ivory: '#FFF3DC', gold: '#F2C35B', maroon: '#6e1f33', maroonDeep: '#4a1424', wine: '#8c2a43' };
